/**
 * Start EC-515 [FE]
 * Create quote cart page along with delete functionality and UI
 * Fixed mobile layout
 * */

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import XCircle from "@insite/mobius/Icons/XCircle";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { addToCartEvent, viewCart } from "@WhiteCap/Common/Utillities/CustomTracking";
import guestRFQCartHandler from "@WhiteCap/Handlers/GuestRFQ/GuestRFQCartHandler";
import useRFQCartProduct, { NewProductModel } from "@WhiteCap/Hooks/useRequestForQuote";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    buttonText = "buttonText",
    link = "link",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.buttonText]: string;
        [fields.link]: LinkFieldValue;
    };
}

const mapStateToProps = (state: CustomApplicationState) => ({
    quoteCount: state.WhiteCap.GuestRFQCart.quoteCount,
    isAuthenticated: getSession(state).isAuthenticated,
    currencyCode: state.context.session.currency?.currencyCode,
});

const mapDispatchToProps = {
    guestRFQCartHandler,
};

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfQCartlineStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    textContainer?: InjectableCss;
    productBrand?: ProductBrandStyles;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    countAndRemoveBlock?: InjectableCss;
    productGridContainer?: GridContainerProps;
    deleteIcon?: IconPresentationProps;
    actionContainer?: InjectableCss;
    cartWrapper?: InjectableCss;
    productImage?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    removeIconWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    nextButonWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    mobileStickyBtn?: InjectableCss;
}

export const rfqCartlineStyles: RfQCartlineStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
            width: 100%;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    textContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 5px;
            a,
            span {
                padding-bottom: 1px;
                display: inline-block;
            }
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 7px;
                    `,
                    css`
                        margin-top: 7px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    countAndRemoveBlock: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            padding: 0 10px 10px;
        `,
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 20px;
        `,
    },
    productGridContainer: {
        css: css`
            position: relative;
            background: ${WhitecapColors.white};
            border-top: 1px solid ${getColor("common.border")};
            padding: 16px;
            position: relative;
            margin-bottom: 20px;
        `,
    },
    deleteIcon: {
        css: css`
            position: absolute;
            cursor: pointer;
            top: 144px;
            right: 28px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        bottom: 17px;
                        top: unset;
                    `,
                    css`
                        bottom: 17px;
                        top: unset;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    actionContainer: {
        css: css`
            position: relative;
            background: ${WhitecapColors.white};
            padding: 0 15px 15px 15px;
            position: relative;
            margin-bottom: 20px;
            width: 100%;
            display: flex;
            justify-content: center;
            flex-direction: column;
            gap: 15px;
        `,
    },
    cartWrapper: {
        css: css`
            padding-top: 20px;
        `,
    },
    productImage: {
        css: css`
            max-width: 167px;
            object-fit: contain;
            width: 100%;
            height: 100%;
            max-height: 187px;
            margin: 0px;
        `,
    },
    removeCartLineIcon: { src: XCircle },
    removeIconWrapper: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
        `,
    },
    nextButonWrapper: {
        css: css`
            ${({ theme }) => breakpointMediaQueries(theme, [css``, css``, null, null, null])}
        `,
    },
    infoWrapper: {
        css: css`
            margin-bottom: 10px;
            padding: 15px;
        `,
    },
    mobileStickyBtn: {
        css: css`
            display: block;
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            z-index: 10;
            button {
                width: 100%;
                border-radius: 0;
            }
        `,
    },
};
const StyledSection = getStyledWrapper("section");
const styles = rfqCartlineStyles;

const RfQCartline = ({ history, fields, guestRFQCartHandler, isAuthenticated, quoteCount, id, currencyCode }: Props) => {
    // If user is Authenticated it's will redirect to cart page
    if (isAuthenticated) {
        history.push("/cart");
    }
    const { cart, updateQtyFromCart, deleteFromLocalStorage, removeQuoteCartFromLocalStorage } = useRFQCartProduct();
    const link = useGetLink(fields.link);
    const [isSticky, setIsSticky] = useState(false);
    const [qtyInputs, setQtyInputs] = useState<{ [productId: string]: string }>({});

    useEffect(() => {
        if (cart && cart.products?.length) {
            const itemData = cart.products.map(product => ({
                item_id: product.productNumber,
                item_name: product.productTitle,
                item_brand: product.brand?.name ? product.brand?.name : "",
                price: "0", 
                quantity: product.qty,
            }));

            viewCart({
                event: "view_cart",
                ecommerce: {
                    currency: currencyCode,
                    value: 0,
                    items: itemData,
                },
            });
        }
    }, [cart]);

    
    useEffect(() => {
        const handleScroll = () => {
            const button = document.querySelector(".mobile-sticky-button");
            const footer = document.querySelector("footer");

            // Check if the button is sticky
            if (button) {
                const rect = button.getBoundingClientRect();
                if (rect.top <= window.innerHeight) {
                    setIsSticky(true);
                } else {
                    setIsSticky(false);
                }
            }

            // If footer exists, update padding-bottom
            if (footer) {
                if (isSticky) {
                    footer.style.setProperty("padding-bottom", "40px", "important");
                } else {
                    footer.style.removeProperty("padding-bottom");
                }
            }
        };

        window.addEventListener("scroll", handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [isSticky]);

    useEffect(() => {
        if (cart) {
            guestRFQCartHandler({ quoteCount: cart.quoteCount });
        }
    }, [cart]);

    const qtyChangeHandler = (product: NewProductModel, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.includes(".") || value.includes(",")) {
            return; // Stop execution if a decimal is detected
        }

        if (/^\d*$/.test(value)) {
            updateQtyFromCart(product, Number(e.target.value));
        }
    };

    const handleQtyChange = (productId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // Allow empty string (for backspace), digits only
        if (value === "" || (/^\d+$/.test(value) && !value.includes(".") && !value.includes(","))) {
            setQtyInputs(prev => ({
                ...prev,
                [productId]: value,
            }));
        }
    };

    const handleQtyBlur = (product: NewProductModel) => {
        const value = qtyInputs[product.id] ?? product.qty.toString();
        const numericValue = Number(value) > 0 ? Number(value) : 1;

        const prevQty = product.qty;
        
        updateQtyFromCart(product, numericValue); // Commit to parent
        setQtyInputs(prev => ({
            ...prev,
            [product.id]: numericValue.toString(),
        }));

        if (numericValue > prevQty) {
            addToCartEvent({
                event: "add_to_cart",
                ecommerce: {
                    currency: currencyCode,
                    value: 0,
                    items: [
                        {
                            item_name: product?.productTitle,
                            item_id: product?.productNumber,
                            quantity: numericValue,
                            price: 0,
                            item_brand: product?.brand?.name ? product?.brand?.name : "",
                        },
                    ],
                },
            });
        }
    };

    const redirectNext = () => {

        const itemData = cart.products.map(product => ({
            item_id: product.productNumber,
            item_name: product.productTitle,
            item_brand: product.brand?.name ? product.brand?.name : "",
            price: "0", 
            quantity: product.qty,
        }));

        viewCart({
            event: "begin_checkout",
            ecommerce: {
                currency: currencyCode,
                value: 0,
                items: itemData,
            },
        });

        if (link.url) {history.push(link.url);}
    };

    if (!cart.products?.length) {
        return (
            <GridItem width={12}>
                <StyledSection {...styles.centeringWrapper}>
                    <Icon {...styles.noCartLinesIcon} />
                    <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                        {siteMessage("Cart_NoOrderLines")}
                    </Typography>
                </StyledSection>
            </GridItem>
        );
    }

    return (
        <GridContainer {...styles.cartWrapper} id="BackToTop">
            <Hidden above="md">
                <StyledWrapper {...styles.infoWrapper}>
                    <Typography className="title">{fields.title}</Typography>
                </StyledWrapper>
            </Hidden>

            <GridItem width={[12, 12, 12, 8, 8]} className="main-container">
                {cart.products && cart.products.length && (
                    <GridContainer>
                        <StyledWrapper {...styles.countAndRemoveBlock}>
                            <Typography>
                                {quoteCount} {translate("Items")}
                            </Typography>
                            <Link
                                onClick={removeQuoteCartFromLocalStorage}
                                data-test-selector="cartlineHeader_removeAll"
                            >
                                {translate("Remove All")}
                            </Link>
                        </StyledWrapper>
                        <StyledWrapper
                            css={css`
                                width: 100%;
                            `}
                        >
                            {cart.products.map(product => (
                                <GridContainer {...styles.productGridContainer} key={product.id}>
                                    {/* To display an image in full width on mobile using a 12-column grid */}
                                    <GridItem
                                        width={[12, 12, 3, 3, 3]}
                                        style={{ alignItems: "center", justifyContent: "center" }}
                                    >
                                        <Img
                                            {...styles.productImage}
                                            src={product.mediumImagePath}
                                            alt={product.imageAltText}
                                        />
                                    </GridItem>
                                    <GridItem width={[12, 12, 8, 8, 8]}>
                                        <StyledWrapper {...styles.textContainer}>
                                            {/* Integrate a product brand component to showcase the brand on the RFQ cart page */}
                                            {product.brand && (
                                                <ProductBrand
                                                    brand={product.brand}
                                                    extendedStyles={styles.productBrand}
                                                />
                                            )}

                                            <Link href={`Catalog${product.canonicalUrl}`}>
                                                <Typography
                                                    css={css`
                                                        color: inherit;
                                                    `}
                                                >
                                                    {product.productTitle}
                                                </Typography>
                                            </Link>
                                            <Typography>
                                                {translate("Product#: ")}
                                                <strong>{product.productNumber}</strong>
                                            </Typography>
                                            

                                            <>
                                                {product.manufacturerItem && (
                                                    <Typography>
                                                        {translate("MFG#: ")}
                                                        <strong>{product.manufacturerItem}</strong>
                                                    </Typography>
                                                )}
                                            </>
                                            <Typography>
                                                {translate("UOM: ")}
                                                <strong>
                                                    {product.unitOfMeasures
                                                        ? product.unitOfMeasures.find(item => item.isDefault)
                                                              ?.unitOfMeasureDisplay
                                                        : ""}
                                                </strong>
                                            </Typography>

                                            <GridContainer {...styles.qtyWrapper}>
                                                <GridItem
                                                    width={12}
                                                    css={css`
                                                        padding-top: 9px;
                                                        padding-bottom: 0px;
                                                    `}
                                                >
                                                    <TextField
                                                        label={translate("QTY")}
                                                        value={qtyInputs[product.id] ?? product.qty.toString()} // use local state if available
                                                        type="number"
                                                        min={0}
                                                        max={999999}
                                                        data-test-selector="product_qtyOrdered"
                                                        onChange={e => handleQtyChange(product.id, e)}
                                                        onBlur={() => handleQtyBlur(product)}
                                                        inputMode="numeric"
                                                        pattern="\d*"
                                                        onKeyPress={event => {
                                                            if (event.key === "." || event.key === ",") {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                        cssOverrides={{
                                                            inputSelect: css`
                                                                width: 95px;
                                                            `,
                                                            formField: css`
                                                                display: flex;
                                                                flex-direction: row;
                                                                align-items: center;
                                                                label {
                                                                    width: 64px;
                                                                    margin-top: 9px;
                                                                }
                                                            `,
                                                        }}
                                                    />
                                                </GridItem>
                                                <GridItem width={12} {...styles.removeIconWrapper}>
                                                    <Clickable
                                                        onClick={() => deleteFromLocalStorage(product.id)}
                                                        data-test-selector="cartline_removeLine"
                                                    >
                                                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                                        <IconMemo {...styles.removeCartLineIcon} />
                                                    </Clickable>
                                                </GridItem>
                                            </GridContainer>
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            ))}
                        </StyledWrapper>

                        {/* EC-614 Implement from Back to bottom on qoute cart page  */}
                        {/* Add BackTOTop widget into zone */}
                        <StyledWrapper
                            css={css`
                                width: 100%;
                                display: flex;
                                justify-content: center;
                            `}
                        >
                            <Zone contentId={id} zoneName="BackToTop"></Zone>
                        </StyledWrapper>
                    </GridContainer>
                )}
            </GridItem>

            <GridItem
                width={[12, 12, 12, 4, 4]}
                {...styles.nextButonWrapper}
                css={css`
                    padding-top: 0px;
                `}
            >
                <Hidden below="md">
                    {cart.products && cart.products.length !== 0 && (
                        <StyledWrapper {...styles.actionContainer}>
                            <Button onClick={redirectNext}>{fields.buttonText}</Button>
                            <Typography className="title">{fields.title}</Typography>
                        </StyledWrapper>
                    )}
                </Hidden>
            </GridItem>

            <Hidden above="md">
                <StyledWrapper {...styles.mobileStickyBtn}>
                    <Button className="mobile-sticky-button" onClick={redirectNext}>
                        {fields.buttonText}
                    </Button>
                </StyledWrapper>
            </Hidden>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfQCartline)),
    definition: {
        displayName: "RFQ Cartline",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "link",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Continue",
                fieldType: "General",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
/**
 * End EC-515 [FE]
 * Create quote cart page along with delete functionality and UI
 * */